import React, { useState } from "react";
import { Box, Typography, Grid, CircularProgress, Divider } from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { ClearRounded, SaveRounded } from "@mui/icons-material";
import CustomButton from "../../common/CustomButton";
import CustomTextField from "../../common/CustomTextField";
import CustomTextarea from "../../common/CustomTextarea";
import { genericPost } from '../../../services/authServices';
import { CREATE_IMPORT_SHIPPING_BOOKING } from '../../../services/apiRoutes';
import CustomAutocomplete from "../../common/CustomAutoComplete";
import CustomStyle from "../../common/CustomStyle";
import CustomPrefixSuffixField from "../../common/CustomPrefixSuffixField";
import { t } from "../../../constants/translationHelper";

const ValidationSchema = Yup.object().shape({
    shippingTypeId: Yup.number().required('Shipping Type is required'),
    portOfLoading: Yup.number()
        .typeError('Port of Loading is required')
        .required('Port of Loading is required'),
    portOfDestination: Yup.number()
        .typeError('Port of Destination is required')
        .required('Port of Destination is required'),
    directIndirect: Yup.string().required('Direct/Indirect is required'),
    shippingLine: Yup.string().required('Shipping Line is required'),
    seaFreight: Yup.string().required('Sea Freight is required'),
    currencyId: Yup.number().required('Currency is required'),
    etdDate: Yup.date().required('ETD Date is required'),
    cutOffDate: Yup.date().required('Cut-Off Date is required'),
    etaDate: Yup.date().required('ETA Date is required'),
    destinationCharges: Yup.number().required('Destination Charges are required'),
    cfsLocalCharges: Yup.number().required('CFS Local Charges are required'),
    shippingLineCharges: Yup.number().required('Shipping Line Charges are required'),
    blCharges: Yup.number().required('BL Charges are required'),
    numberOfContainers: Yup.number().required('Number of Containers is required'),
    containerType: Yup.number().required('Container Type is required'),
    freightForwardAgencyId: Yup.number().required('Freight Forward Agency is required'),
    clearingAgencyId: Yup.number().required('Clearing Agency is required'),
    freeDaysAtDestination: Yup.number().required('Free Days at Destination is required'),
    proformaInvExpId: Yup.string().required("proforma Inv. is required"),
    qty: Yup.number()
        .typeError('Quantity must be a number')
        .positive('Quantity must be greater than 0')
        .required('Quantity is required'),

    perContainer: Yup.number()
        .typeError('Per Container must be a number')
        .positive('Per Container must be greater than 0')
        .required('Per Container is required'),
    bookingNumber: Yup.string().required("Booking Number is required"),

});

const CreateShippingBooking = ({ handleClose, setMessage, bookingNumber, portOfLoading, portMasters, portOfDestination, proformaInvoiceList, fetchButtonList, shipmentOrgId, cifFobId, currencyId, currencySym, currencySymbol, baseCurrencyId, fetchData, shippingLines, fclData, directListData, freightForwardAgencyData, customClearingAgencyData, cifFobList, currencyList, clientCompanyId, commanShipmentId, shipmentId }) => {
    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const payload = {
                commanShipmentId: values.commanShipmentId,
                shipmentOrgId: values.shipmentOrgId,
                proformaInvExpId: values.proformaInvExpId,
                proformaInvoiceId: values.proformaInvoiceId,
                shipmentId: values.shipmentId,
                clientCompanyId: values.clientCompanyId,
                bookingNumber: values.bookingNumber,
                shippingTypeId: values.shippingTypeId,
                portOfLoading: values.portOfLoading,
                portOfDestination: values.portOfDestination,
                directIndirect: values.directIndirect,
                shippingLine: values.shippingLine,
                conversionRate: values.conversionRate,
                seaFreight: values.seaFreight,
                currencyId: values.currencyId,
                baseCurrencyId: values.baseCurrencyId,
                etdDate: values.etdDate,
                cutOffDate: values.cutOffDate,
                etaDate: values.etaDate,
                destinationCharges: values.destinationCharges,
                cfsLocalCharges: values.cfsLocalCharges,
                shippingLineCharges: values.shippingLineCharges,
                blCharges: values.blCharges,
                numberOfContainers: values.numberOfContainers,
                containerType: values.containerType,
                freightForwardAgencyId: values.freightForwardAgencyId,
                clearingAgencyId: values.clearingAgencyId,
                freeDaysAtDestination: values.freeDaysAtDestination,
                note: values.note,
                qty: values.qty,
                perContainer: values.perContainer,

            };

            const response = await genericPost(CREATE_IMPORT_SHIPPING_BOOKING, payload);

            if (response.status === 201) {
                setMessage({
                    open: true,
                    text: response?.data?.message || "Booking created successfully",
                    type: "success",
                });
                setTimeout(() => {
                    fetchData();
                    fetchButtonList();
                    handleClose();
                }, 1000);
            } else {
                alert('Something went wrong');
            }
        } catch (error) {
            alert('Something went wrong');
        } finally {
            setSubmitting(false);
        }
    };

    const [formData] = useState({
        clientCompanyId: clientCompanyId,
        commanShipmentId: commanShipmentId,
        shipmentOrgId: shipmentOrgId,
        shipmentId: shipmentId,
        currencyId: currencyId,
        shippingTypeId: cifFobId,
        baseCurrencyId: baseCurrencyId,
        portOfLoading: parseInt(portOfLoading || ''),
        portOfDestination: parseInt(portOfDestination || ''),
        directIndirect: '',
        bookingNumber: bookingNumber,
        proformaInvExpId: '',
        proformaInvoiceId: '',
        shippingLine: '',
        conversionRate: '',
        seaFreight: '',
        etdDate: '',
        cutOffDate: '',
        etaDate: '',
        destinationCharges: '',
        cfsLocalCharges: '',
        shippingLineCharges: '',
        blCharges: '',
        numberOfContainers: '',
        containerType: '',
        freightForwardAgencyId: '',
        clearingAgencyId: '',
        freeDaysAtDestination: '',
        note: '',
        qty: '',
        perContainer: '',


    });

    return (
        <Box sx={{ mx: 'auto', mt: 0 }}>
            <Formik
                initialValues={formData}
                validationSchema={ValidationSchema}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting, errors, touched, handleChange, setFieldValue, values }) => (
                    <Form>
                        <Grid container spacing={1} style={CustomStyle.container}>
                            <Grid item xs={4} md={4}>
                                <Typography sx={{ fontWeight: 600 }} style={CustomStyle.title}>
                                    {t("Create Shipping  Booking")}
                                </Typography>
                            </Grid>
                            <Grid item xs={8} md={8} style={CustomStyle.buttonContainer}>
                                <Box style={CustomStyle.buttonBox}>
                                    <CustomButton
                                        startIcon={<SaveRounded />}
                                        title={isSubmitting ? <CircularProgress size={20} color="inherit" /> : "Save"}
                                        size="small"
                                        variant="contained"
                                        type="submit"
                                        disabled={isSubmitting}
                                    />
                                    <CustomButton
                                        startIcon={<SaveRounded />}
                                        title=" Save and New"
                                        size="small"
                                        variant="contained"
                                    />
                                    <CustomButton
                                        startIcon={<ClearRounded />}
                                        title="Cancel"
                                        size="small"
                                        variant="contained"
                                        onClick={handleClose}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                        <Divider />
                        <Grid container spacing={2} sx={{ px: "5%", paddingTop: "5px" }}>
                            <Grid item xs={12} md={3}>
                                <CustomAutocomplete
                                    options={proformaInvoiceList}
                                    fieldLabel="Proforma Invoice"
                                    required={true}
                                    getOptionLabel={(option) => option.title}
                                    value={proformaInvoiceList.find((option) => option.id === values.proformaInvExpId) || null}
                                    onChange={(e, newValue) => {
                                        if (newValue) {
                                            setFieldValue('proformaInvExpId', newValue.id);
                                            setFieldValue('qty', newValue.totalQty || 0);
                                            setFieldValue("perContainer", "");
                                            setFieldValue("numberOfContainers", "");

                                        } else {
                                            setFieldValue('proformaInvExpId', '');
                                            setFieldValue('qty', '');
                                            setFieldValue("perContainer", "");
                                            setFieldValue("numberOfContainers", "");

                                        }
                                    }}
                                    placeholder="Select Proforma Invoice"
                                    error={touched.proformaInvExpId && !!errors.proformaInvExpId}
                                    helperText={touched.proformaInvExpId && errors.proformaInvExpId}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <CustomAutocomplete
                                    options={cifFobList}
                                    fieldLabel="Select Type"
                                    required={true}
                                    getOptionLabel={(option) => option.title}
                                    value={cifFobList.find((option) => option.id === values.shippingTypeId) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('shippingTypeId', newValue ? newValue.id : '')
                                    }
                                    placeholder="Enter Select Type"
                                    error={touched.shippingTypeId && !!errors.shippingTypeId}
                                    helperText={touched.shippingTypeId && errors.shippingTypeId}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <CustomAutocomplete
                                    options={directListData}
                                    fieldLabel="Direct/Indirect"
                                    required={true}
                                    getOptionLabel={(option) => option.title}
                                    value={directListData.find((option) => option.id === values.directIndirect) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('directIndirect', newValue ? newValue.id : '')
                                    }
                                    placeholder="Select Direct/Indirect"
                                    error={touched.directIndirect && !!errors.directIndirect}
                                    helperText={touched.directIndirect && errors.directIndirect}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <CustomTextField
                                    placeholder="Enter Booking Number"
                                    name="bookingNumber"
                                    fieldLabel="Booking Number"
                                    required={true}
                                    value={values.bookingNumber}
                                    onChange={handleChange}
                                    sx={CustomStyle.readOnly}
                                    readOnly={true}
                                    error={touched.bookingNumber && !!errors.bookingNumber}
                                    helperText={touched.bookingNumber && errors.bookingNumber}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomAutocomplete
                                    options={portMasters}
                                    fieldLabel="Port Of Loading"
                                    getOptionLabel={(option) => option.title}
                                    value={portMasters.find((option) => option.id === values.portOfLoading) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('portOfLoading', newValue ? newValue.id : '')
                                    }
                                    placeholder="Select Port Of Loading"
                                    required={true}
                                    error={touched.portOfLoading && !!errors.portOfLoading}
                                    helperText={touched.portOfLoading && errors.portOfLoading}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomAutocomplete
                                    options={portMasters}
                                    fieldLabel="Port Of Destination"
                                    getOptionLabel={(option) => option.title}
                                    value={portMasters.find((option) => option.id === values.portOfDestination) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('portOfDestination', newValue ? newValue.id : '')
                                    }
                                    placeholder="Select Port Of Destination"
                                    required={true}
                                    error={touched.portOfDestination && !!errors.portOfDestination}
                                    helperText={touched.portOfDestination && errors.portOfDestination}
                                />

                            </Grid>

                            <Grid item xs={12} md={6}>
                                <CustomAutocomplete
                                    options={shippingLines}
                                    fieldLabel="Shipping Line"
                                    required={true}
                                    getOptionLabel={(option) => option.title}
                                    value={shippingLines.find((option) => option.id === values.shippingLine) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('shippingLine', newValue ? newValue.id : '')
                                    }
                                    placeholder="Select Shipping Line"
                                    error={touched.shippingLine && !!errors.shippingLine}
                                    helperText={touched.shippingLine && errors.shippingLine}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <CustomAutocomplete
                                    options={currencyList}
                                    fieldLabel="Select Currency"
                                    required={true}
                                    getOptionLabel={(option) => option.title}
                                    value={currencyList.find((option) => option.id === values.currencyId) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('currencyId', newValue ? newValue.id : '')
                                    }
                                    placeholder="Select Currency"
                                    error={touched.currencyId && !!errors.currencyId}
                                    helperText={touched.currencyId && errors.currencyId}
                                    sx={CustomStyle.readOnly}
                                    readOnly={true}
                                />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <CustomPrefixSuffixField
                                    prefix={currencySym}
                                    placeholder="Sea Freight"
                                    tagLine="Per Con."
                                    name="seaFreight"
                                    fieldLabel="Sea Freight"
                                    required={true}
                                    type="number"
                                    value={values.seaFreight}
                                    onChange={handleChange}
                                    error={touched.seaFreight && !!errors.seaFreight}
                                    helperText={touched.seaFreight && errors.seaFreight}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <CustomTextField
                                    placeholder="Enter ETD Date"
                                    type="date"
                                    name="etdDate"
                                    fieldLabel="ETD Date"
                                    required={true}
                                    value={values.etdDate}
                                    onChange={handleChange}
                                    error={touched.etdDate && !!errors.etdDate}
                                    helperText={touched.etdDate && errors.etdDate}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <CustomTextField
                                    placeholder="Enter Cut-Off Date"
                                    type="date"
                                    name="cutOffDate"
                                    fieldLabel="Cut-Off Date"
                                    required={true}
                                    value={values.cutOffDate}
                                    onChange={handleChange}
                                    error={touched.cutOffDate && !!errors.cutOffDate}
                                    helperText={touched.cutOffDate && errors.cutOffDate}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <CustomTextField
                                    placeholder="Enter ETA Date"
                                    type="date"
                                    name="etaDate"
                                    fieldLabel="ETA Date"
                                    required={true}
                                    value={values.etaDate}
                                    onChange={handleChange}
                                    error={touched.etaDate && !!errors.etaDate}
                                    helperText={touched.etaDate && errors.etaDate}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <CustomTextField
                                    placeholder="Enter Free Days at Destination"
                                    name="freeDaysAtDestination"
                                    fieldLabel="Free Days at Destination"
                                    required={true}
                                    value={values.freeDaysAtDestination}
                                    onChange={handleChange}
                                    error={touched.freeDaysAtDestination && !!errors.freeDaysAtDestination}
                                    helperText={touched.freeDaysAtDestination && errors.freeDaysAtDestination}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomAutocomplete
                                    options={customClearingAgencyData}
                                    fieldLabel="Clearing Agency (CHA)"
                                    required={true}
                                    getOptionLabel={(option) => option.title}
                                    value={customClearingAgencyData.find((option) => option.id === values.clearingAgencyId) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('clearingAgencyId', newValue ? newValue.id : '')
                                    }
                                    placeholder="Select Clearing Agency"
                                    error={touched.clearingAgencyId && !!errors.clearingAgencyId}
                                    helperText={touched.clearingAgencyId && errors.clearingAgencyId}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomAutocomplete
                                    options={freightForwardAgencyData}
                                    fieldLabel="Freight Forward Agency"
                                    required={true}
                                    getOptionLabel={(option) => option.title}
                                    value={freightForwardAgencyData.find((option) => option.id === values.freightForwardAgencyId) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('freightForwardAgencyId', newValue ? newValue.id : '')
                                    }
                                    placeholder="Select Freight Forward Agency"
                                    error={touched.freightForwardAgencyId && !!errors.freightForwardAgencyId}
                                    helperText={touched.freightForwardAgencyId && errors.freightForwardAgencyId}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <CustomPrefixSuffixField
                                    prefix={currencySymbol}
                                    tagLine="(In Base Currency)"
                                    placeholder="Enter Others Charges"
                                    name="destinationCharges"
                                    type="number"
                                    fieldLabel="Others Charges"
                                    required={true}
                                    value={values.destinationCharges}
                                    onChange={handleChange}
                                    error={touched.destinationCharges && !!errors.destinationCharges}
                                    helperText={touched.destinationCharges && errors.destinationCharges}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomPrefixSuffixField
                                    prefix={currencySymbol}
                                    tagLine="(In Base Currency)"
                                    placeholder="Enter CFS Local Charges"
                                    name="cfsLocalCharges"
                                    fieldLabel="CFS/Local Charges"
                                    type="number"
                                    required={true}
                                    value={values.cfsLocalCharges}
                                    onChange={handleChange}
                                    error={touched.cfsLocalCharges && !!errors.cfsLocalCharges}
                                    helperText={touched.cfsLocalCharges && errors.cfsLocalCharges}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomPrefixSuffixField
                                    prefix={currencySymbol}
                                    tagLine="(In Base Currency)"
                                    placeholder="Enter Shipping Line Charges"
                                    name="shippingLineCharges"
                                    fieldLabel="Shipping Line Charges"
                                    required={true}
                                    type="number"
                                    value={values.shippingLineCharges}
                                    onChange={handleChange}
                                    error={touched.shippingLineCharges && !!errors.shippingLineCharges}
                                    helperText={touched.shippingLineCharges && errors.shippingLineCharges}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <CustomPrefixSuffixField
                                    prefix={currencySymbol}
                                    tagLine="(In Base Currency)"
                                    placeholder="Enter BL Charges"
                                    name="blCharges"
                                    fieldLabel="BL Charges"
                                    required={true}
                                    type="number"
                                    value={values.blCharges}
                                    onChange={handleChange}
                                    error={touched.blCharges && !!errors.blCharges}
                                    helperText={touched.blCharges && errors.blCharges}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <CustomAutocomplete
                                    options={fclData}
                                    fieldLabel="20FCL/40FCL"
                                    required={true}
                                    getOptionLabel={(option) => option.title}
                                    value={fclData.find((option) => option.id === values.containerType) || null}
                                    onChange={(e, newValue) =>
                                        setFieldValue('containerType', newValue ? newValue.id : '')
                                    }
                                    placeholder="Select 20FCL/40FCL"
                                    error={touched.containerType && !!errors.containerType}
                                    helperText={touched.containerType && errors.containerType}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <CustomTextField
                                    placeholder="Enter Quantity"
                                    name="qty"
                                    fieldLabel="Quantity"
                                    type="number"
                                    required={true}
                                    value={values.qty}
                                    onChange={(e) => {
                                        const newQty = parseFloat(e.target.value) || 0;
                                        const totalQty = parseFloat(
                                            proformaInvoiceList.find(
                                                (inv) => inv.id === values.proformaInvExpId
                                            )?.totalQty || 0
                                        );

                                        // ✅ Check if entered qty is greater than totalQty
                                        if (totalQty && newQty > totalQty) {
                                            alert(`Entered Quantity (${newQty}) cannot be greater than Total Quantity (${totalQty})`);
                                            return; // stop processing further
                                        }

                                        setFieldValue("qty", newQty);

                                        // Auto calculate numberOfContainers if perContainer > 0
                                        const perContainer = parseFloat(values.perContainer) || 0;
                                        if (newQty > 0 && perContainer > 0) {
                                            const calculated = (newQty / perContainer).toFixed(2);
                                            setFieldValue("numberOfContainers", calculated);
                                        } else {
                                            setFieldValue("numberOfContainers", "");
                                        }
                                    }}
                                    error={touched.qty && !!errors.qty}
                                    helperText={touched.qty && errors.qty}
                                />

                            </Grid>

                            <Grid item xs={12} md={3}>
                                <CustomTextField
                                    placeholder="Enter Per Container"
                                    name="perContainer"
                                    fieldLabel="Per Container"
                                    type="number"
                                    required={true}
                                    value={values.perContainer}
                                    onChange={(e) => {
                                        const newPerContainer = e.target.value;
                                        setFieldValue('perContainer', newPerContainer);

                                        const qty = parseFloat(values.qty) || 0;
                                        const perContainer = parseFloat(newPerContainer) || 0;

                                        if (qty > 0 && perContainer > 0) {
                                            const calculated = (qty / perContainer).toFixed(2);
                                            setFieldValue('numberOfContainers', calculated);
                                        } else {
                                            setFieldValue('numberOfContainers', '');
                                        }
                                    }}
                                    error={touched.perContainer && !!errors.perContainer}
                                    helperText={touched.perContainer && errors.perContainer}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <CustomTextField
                                    placeholder="Enter Number of Containers"
                                    name="numberOfContainers"
                                    fieldLabel="Number of Containers"
                                    required={true}
                                    type="number"
                                    value={values.numberOfContainers}
                                    onChange={handleChange}
                                    sx={CustomStyle.readOnly}
                                    readOnly={true}
                                    error={touched.numberOfContainers && !!errors.numberOfContainers}
                                    helperText={touched.numberOfContainers && errors.numberOfContainers}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <CustomPrefixSuffixField
                                    prefix={currencySymbol}
                                    tagLine="(In Base Currency)"
                                    placeholder="Enter Conversion Rate"
                                    name="conversionRate"
                                    fieldLabel="Conversion Rate"
                                    type="number"
                                    value={values.conversionRate}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <CustomTextarea
                                    placeholder="Enter Note"
                                    name="note"
                                    fieldLabel="Note"
                                    value={values.note}
                                    onChange={handleChange}
                                />
                            </Grid>
                        </Grid>
                    </Form>
                )}
            </Formik>
        </Box>
    );
};

export default CreateShippingBooking;
